/**
 * TipTap Toolbar Handler
 * Shared functionality for TipTap editor toolbars
 */

import {
  showMathModal,
  hideMathModal,
  insertMathFromModal,
  updateMathButtonState,
  handleMathEditorAction,
  setupMathListeners,
} from './mathEditor.js'
import {
  handleDrawingEditorAction,
  setupDrawingListeners,
} from './drawingEditor.js'

// Highlight color mapping
const HIGHLIGHT_COLORS = {
  yellow: '#fef08a',
  green: '#bbf7d0',
  pink: '#fbcfe8',
  blue: '#bfdbfe',
}

/**
 * Update toolbar button states based on editor state
 */
function updateToolbar(editor, toolbar, headingSelect) {
  if (!editor || !toolbar) return

  // Update heading select
  if (headingSelect) {
    const level = editor.getAttributes('heading')?.level
    headingSelect.value = level ? level.toString() : ''
  }

  // Update button states
  toolbar.querySelectorAll('button').forEach((button) => {
    const action = button.getAttribute('data-action')
    if (!action) return

    let isActive = false

    // Check if action is active
    if (action === 'bold') {
      isActive = editor.isActive('bold')
    } else if (action === 'italic') {
      isActive = editor.isActive('italic')
    } else if (action === 'bulletList') {
      isActive = editor.isActive('bulletList')
    } else if (action === 'orderedList') {
      isActive = editor.isActive('orderedList')
    } else if (action === 'blockquote') {
      isActive = editor.isActive('blockquote')
    } else if (action === 'mathEditor') {
      isActive = updateMathButtonState(editor, button)
    } else if (action === 'remove-highlight') {
      isActive = editor.isActive('highlight')
    } else if (action.startsWith('highlight-')) {
      const color = action.replace('highlight-', '')
      if (HIGHLIGHT_COLORS[color]) {
        const attrs = editor.getAttributes('highlight')
        isActive = attrs && attrs.color === HIGHLIGHT_COLORS[color]
      }
    }

    button.classList.toggle('is-active', isActive)

    // Update highlight menu items active state
    if (button.classList.contains('highlight-menu-item')) {
      const hasHighlight = editor.isActive('highlight')
      if (action === 'remove-highlight') {
        button.classList.toggle('is-active', hasHighlight)
      } else if (action.startsWith('highlight-')) {
        const color = action.replace('highlight-', '')
        if (HIGHLIGHT_COLORS[color]) {
          const attrs = editor.getAttributes('highlight')
          const isColorActive = attrs && attrs.color === HIGHLIGHT_COLORS[color]
          button.classList.toggle('is-active', isColorActive)
        }
      }
    }

    // Enable/disable table menu button based on whether cursor is in a table
    if (button.classList.contains('table-menu-button')) {
      const inTable = editor.isActive('table')
      button.disabled = !inTable
    }
  })
}

/**
 * Handle toolbar button click
 */
function handleToolbarAction(editor, action) {
  if (!editor) return

  if (action === 'bold') {
    editor.chain().focus().toggleBold().run()
  } else if (action === 'italic') {
    editor.chain().focus().toggleItalic().run()
  } else if (action === 'bulletList') {
    editor.chain().focus().toggleBulletList().run()
  } else if (action === 'orderedList') {
    editor.chain().focus().toggleOrderedList().run()
  } else if (action === 'undo') {
    editor.chain().focus().undo().run()
  } else if (action === 'redo') {
    editor.chain().focus().redo().run()
  } else if (action === 'blockquote') {
    editor.chain().focus().toggleBlockquote().run()
  } else if (action === 'mathEditor') {
    // Handled in setupToolbar where we have access to modalId
    return
  } else if (action === 'drawingEditor') {
    // Handled in setupToolbar where we have access to modalId
    return
  } else if (action === 'link') {
    const url = window.prompt('Enter URL:')
    if (url) {
      editor.chain().focus().setLink({ href: url }).run()
    }
  } else if (action === 'insertTable') {
    editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run()
  } else if (action === 'addRowBefore') {
    editor.chain().focus().addRowBefore().run()
  } else if (action === 'addRowAfter') {
    editor.chain().focus().addRowAfter().run()
  } else if (action === 'deleteRow') {
    editor.chain().focus().deleteRow().run()
  } else if (action === 'addColumnBefore') {
    editor.chain().focus().addColumnBefore().run()
  } else if (action === 'addColumnAfter') {
    editor.chain().focus().addColumnAfter().run()
  } else if (action === 'deleteColumn') {
    editor.chain().focus().deleteColumn().run()
  } else if (action === 'deleteTable') {
    editor.chain().focus().deleteTable().run()
  } else if (action === 'remove-highlight') {
    editor.chain().focus().unsetHighlight().run()
  } else if (action.startsWith('highlight-')) {
    const color = action.replace('highlight-', '')
    if (HIGHLIGHT_COLORS[color]) {
      editor
        .chain()
        .focus()
        .toggleHighlight({ color: HIGHLIGHT_COLORS[color] })
        .run()
    }
  }
}

/**
 * Set up toolbar event listeners
 */
function setupToolbar(editor, toolbarId, headingSelectId) {
  const toolbar = document.getElementById(toolbarId)
  const headingSelect = document.getElementById(headingSelectId)
  const toolbarInstanceId = toolbarId.replace('toolbar_', '')
  const mathModalId = `math_modal_${toolbarInstanceId}`
  const drawingModalId = `drawing_modal_${toolbarInstanceId}`
  const drawingCanvasId = `drawing_canvas_${toolbarInstanceId}`

  if (!toolbar || !editor) return

  // Handle toolbar button clicks
  toolbar.addEventListener('click', (e) => {
    const button = e.target.closest('button')
    if (!button) return

    const action = button.getAttribute('data-action')
    if (!action) return

    e.preventDefault()

    // Handle table menu dropdown toggle
    if (action === 'tableMenu') {
      const container = button.closest('.table-menu-container')
      const dropdown = container?.querySelector('.table-menu-dropdown')
      if (dropdown) {
        dropdown.classList.toggle('is-open')
      }
      // Close highlight menu if open
      const highlightDropdown = toolbar.querySelector(
        '.highlight-menu-dropdown'
      )
      if (highlightDropdown) {
        highlightDropdown.classList.remove('is-open')
      }
      return
    }

    // Handle highlight menu dropdown toggle
    if (action === 'highlightMenu') {
      const container = button.closest('.highlight-menu-container')
      const dropdown = container?.querySelector('.highlight-menu-dropdown')
      if (dropdown) {
        dropdown.classList.toggle('is-open')
      }
      // Close table menu if open
      const tableDropdown = toolbar.querySelector('.table-menu-dropdown')
      if (tableDropdown) {
        tableDropdown.classList.remove('is-open')
      }
      return
    }

    // Handle math editor action
    if (action === 'mathEditor') {
      handleMathEditorAction(editor, mathModalId)
      return
    }

    // Handle drawing editor action
    if (action === 'drawingEditor') {
      handleDrawingEditorAction(editor, drawingModalId)
      return
    }

    // Close dropdowns when clicking actions
    if (
      action.startsWith('add') ||
      action.startsWith('delete') ||
      action === 'deleteTable'
    ) {
      const container = toolbar.querySelector('.table-menu-container')
      const dropdown = container?.querySelector('.table-menu-dropdown')
      if (dropdown) {
        dropdown.classList.remove('is-open')
      }
    }

    if (action.startsWith('highlight-') || action === 'remove-highlight') {
      const container = toolbar.querySelector('.highlight-menu-container')
      const dropdown = container?.querySelector('.highlight-menu-dropdown')
      if (dropdown) {
        dropdown.classList.remove('is-open')
      }
    }

    handleToolbarAction(editor, action)
    updateToolbar(editor, toolbar, headingSelect)
  })

  // Close dropdowns when clicking outside
  document.addEventListener('click', (e) => {
    if (!toolbar.contains(e.target)) {
      const tableDropdown = toolbar.querySelector('.table-menu-dropdown')
      if (tableDropdown) {
        tableDropdown.classList.remove('is-open')
      }
      const highlightDropdown = toolbar.querySelector(
        '.highlight-menu-dropdown'
      )
      if (highlightDropdown) {
        highlightDropdown.classList.remove('is-open')
      }
    }
  })

  // Handle heading select change
  if (headingSelect) {
    headingSelect.addEventListener('change', (e) => {
      const level = parseInt(e.target.value)
      if (level) {
        editor.chain().focus().toggleHeading({ level }).run()
      } else {
        editor.chain().focus().setParagraph().run()
      }
      updateToolbar(editor, toolbar, headingSelect)
    })
  }

  // Update toolbar on selection change
  editor.on('selectionUpdate', () => {
    updateToolbar(editor, toolbar, headingSelect)
  })
  editor.on('transaction', () => {
    updateToolbar(editor, toolbar, headingSelect)
  })

  // Initial toolbar update
  updateToolbar(editor, toolbar, headingSelect)

  // Set up math modal event listeners
  setupMathListeners(editor, mathModalId)

  // Set up drawing modal event listeners
  setupDrawingListeners(editor, drawingModalId, drawingCanvasId)
}

// Make functions available globally for use in templates
window.TipTapToolbar = {
  setupToolbar,
  updateToolbar,
  handleToolbarAction,
  showMathModal,
  hideMathModal,
  insertMathFromModal,
}

// Also make math editor functions available for onClick handlers
window.TipTapMathEditor = {
  showMathModal,
  hideMathModal,
  insertMathFromModal,
}
